import { Button, Drawer, Input } from '@dnb/eufemia';
import filter from '@dnb/eufemia/icons/dnb/filter';
import { debounce } from '@portals/shared-frontend/utils';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';

interface ExplorerPageProps {
  title: string;
  filters: ReactNode;
  filterTags: ReactNode;
  children: ReactNode;
}

export function ExplorerPage({ title, filters, filterTags, children }: ExplorerPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('query') ?? '');

  useEffect(() => {
    setQuery(searchParams.get('query') ?? '');
  }, [searchParams]);

  const debounced = useMemo(
    () =>
      debounce((nextQuery: string | undefined) => {
        setSearchParams((prev) => {
          const updated = new URLSearchParams(prev);
          if (nextQuery) {
            updated.set('query', nextQuery);
          } else {
            updated.delete('query');
          }
          updated.set('page', '0');
          return updated;
        });
      }, 300),
    [setSearchParams],
  );

  const searchField = (
    <Input
      icon="loupe"
      onChange={({ value }) => {
        setQuery(value);
        debounced(value);
      }}
      placeholder="Search"
      showClearButton
      stretch
      value={query}
    />
  );

  return (
    <HeroPage banner="explorer" title={title}>
      <HeroMenu>
        <div className="flex flex-col gap-2">
          {searchField}
          <Button
            className="self-start"
            onClick={() => {
              setQuery('');
              setSearchParams(undefined);
            }}
            variant="tertiary"
          >
            Clear Filters
          </Button>
          {filters}
        </div>
      </HeroMenu>
      <HeroContent>
        <HeroContentMenu>
          {searchField}
          <Drawer
            id="tagFilterDrawer"
            title="Filters"
            top="small"
            triggerAttributes={{ icon: filter, text: 'Filters', variant: 'tertiary' }}
          >
            {filters}
          </Drawer>
          {filterTags}
        </HeroContentMenu>
        {children}
      </HeroContent>
    </HeroPage>
  );
}
